import { ChevronDownIcon, XIcon } from "lucide-react";
import React from "react";
import {
  type ButtonProps,
  composeRenderProps,
  Button as RACButton,
  Select as RACSelect,
  type SelectProps as RACSelectProps,
  SelectStateContext,
  SelectValue,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { buttonVariants } from "@/components/ui/button";
import { Pressable } from "@/components/ui/container";
import {
  DropdownItem,
  DropdownSection,
  ListBox,
  type ListBoxProps,
} from "@/components/ui/list-box";
import { Popover } from "@/components/ui/popover";
import {
  composeTailwindRenderProps,
  inputFocusRing,
} from "@/components/ui/utils";

export function Select<T extends object>(props: RACSelectProps<T>) {
  return (
    <RACSelect
      {...props}
      placeholder={props.placeholder || "Select"}
      className={composeTailwindRenderProps(
        props.className,
        "group flex flex-col gap-1",
      )}
    />
  );
}

const triggerStyles = tv({
  extend: inputFocusRing,
  base: [
    "flex h-8 w-full items-center justify-between gap-2 rounded-lg border border-input bg-background px-3 text-start text-sm text-foreground shadow-xs shadow-black/5 [&>span]:min-w-0",
  ],
  variants: {
    isDisabled: {
      false: "group-invalid:border-destructive",
      true: "opacity-50 cursor-not-allowed",
    },
  },
});

export function SelectTriggerButton(props: ButtonProps) {
  return (
    <RACButton
      {...props}
      className={composeRenderProps(props.className, (className, props) =>
        triggerStyles({ ...props, className }),
      )}
    />
  );
}

export function SelectTrigger({
  isClearable,
  ...props
}: ButtonProps & { isClearable?: boolean }) {
  const state = React.useContext(SelectStateContext);
  return (
    <SelectTriggerButton {...props}>
      {({ isDisabled }) => (
        <>
          <SelectValue className="flex-1 text-sm data-placeholder:text-muted-foreground/70 [&_[slot=description]]:hidden truncate" />

          <div className="flex items-center gap-1">
            {isClearable && state?.selectedKey && (
              <Pressable
                onPress={() => state?.setSelectedKey(null)}
                isDisabled={isDisabled}
              >
                <span
                  className={buttonVariants({
                    variant: "ghost",
                    size: "icon-sm",
                    className:
                      "text-muted-foreground group-disabled:opacity-50",
                  })}
                >
                  <XIcon />
                </span>
              </Pressable>
            )}
            <ChevronDownIcon
              aria-hidden
              className="w-4 h-4 text-muted-foreground forced-colors:text-[ButtonText] group-disabled:opacity-50"
            />
          </div>
        </>
      )}
    </SelectTriggerButton>
  );
}

export function SelectList<T extends object>({ ...props }: ListBoxProps<T>) {
  return (
    <Popover
      className="min-w-(--trigger-width) border-input p-0"
      respectScreen={false}
    >
      <ListBox
        {...props}
        className="outline-hidden p-1 max-h-[inherit] overflow-auto [clip-path:inset(0_0_0_0_round_.75rem)]"
      />
    </Popover>
  );
}

export const SelectItem = DropdownItem;
export const SelectSection = DropdownSection;
